
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { UserRound, LogOut, Settings, CreditCard, Loader2, Cookie } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useLogout } from '@/hooks/useLogout';
import { getCurrentUser } from '@/lib/api/auth';
import { openCookiePreferences } from '@/lib/cookieConsent';

interface UserMenuProps {
  isLoggedIn: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ isLoggedIn }) => {
  const { handleLogout, isLoggingOut } = useLogout();
  const profileImageUrl = getCurrentUser()?.profileImageUrl || '';
  if (!isLoggedIn) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="relative rounded-full p-0 h-12 w-12 border-2 border-cs-primary bg-cs-primary/10 hover:bg-cs-primary/20 hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md group focus:ring-2 focus:ring-cs-primary/20 focus:outline-none"
          title="User menu"
        >
          <Avatar className="h-full w-full">
            <AvatarImage src={profileImageUrl} alt="User profile" />
            <AvatarFallback className="bg-cs-primary/20 text-cs-primary group-hover:bg-cs-primary/30 transition-colors duration-200">
              <UserRound className="h-6 w-6 group-hover:scale-110 transition-transform duration-200" />
            </AvatarFallback>
          </Avatar>
          {/* Subtle indicator */}
          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white opacity-100 transition-opacity duration-200"></div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 shadow-xl border border-gray-200 bg-white animate-in slide-in-from-top-2 duration-200">
        <DropdownMenuItem asChild>
          <Link to="/profile?view=hub" className="cursor-pointer flex w-full items-center hover:bg-gray-50 transition-colors duration-200">
            <Settings className="mr-2 h-4 w-4" />
            <span>My Settings</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile?tab=billing" className="cursor-pointer flex w-full items-center hover:bg-gray-50 transition-colors duration-200">
            <CreditCard className="mr-2 h-4 w-4" />
            <span>My Billing Profile</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => openCookiePreferences()}
          className="cursor-pointer flex w-full items-center hover:bg-gray-50 transition-colors duration-200"
        >
          <Cookie className="mr-2 h-4 w-4" />
          <span>Cookie Preferences</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          disabled={isLoggingOut}
          className="cursor-pointer text-red-600 focus:text-red-600 hover:text-red-700 hover:bg-red-50 transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed focus:bg-red-50 focus:outline-none"
        >
          {isLoggingOut ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <LogOut className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
          )}
          <span className="font-medium">{isLoggingOut ? 'Logging out...' : 'Log out'}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
